import { memo, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCaseDetailsViewPermission } from '@lp/core/hooks/useCaseDetailsViewPermission';
import { declineStatuses, organOnOcsStatuses } from '@features/components/CaseCard/constants';
import { formatCaseData } from '@features/components/CaseCard/utils';
import { Case, CaseStatus } from '@/api/apis/case';
import {
  BaseCaseCard,
  CaseCardWithInvoice,
  CaseCardWithClinicalInfo,
  CaseCardWithServiceDeclined,
} from './components/Cards';
import { isRandomizationScs } from '@/utils/helpers';

type CaseCardProps = {
  caseData: Case;
  onClick?: () => void;
  onDuplicateClick?: (duplicate: Case) => void;
};

const CaseCardComponent = ({ caseData, onClick, onDuplicateClick }: CaseCardProps) => {
  const { status, randomization } = caseData ?? {};
  const viewForbidden = useCaseDetailsViewPermission(caseData);
  const { baseCaseData, operationTimeline, clinicalData } = useMemo(() => formatCaseData(caseData), [caseData]);

  const handleCardClick = () => {
    if (viewForbidden) {
      toast.error('You have no access to this case');
      return;
    }
    onClick?.();
  };

  if (!caseData) return null;
  if (organOnOcsStatuses.includes(status) && !isRandomizationScs(randomization)) {
    return (
      <CaseCardWithClinicalInfo
        caseData={baseCaseData}
        operationTimeline={operationTimeline}
        clinical={clinicalData}
        onClick={handleCardClick}
      />
    );
  }
  if (declineStatuses.includes(status)) {
    return <CaseCardWithServiceDeclined caseData={baseCaseData} onClick={handleCardClick} />;
  }
  if (status === CaseStatus.Finished) {
    return <CaseCardWithInvoice caseData={baseCaseData} onClick={handleCardClick} />;
  }
  return <BaseCaseCard caseData={baseCaseData} onClick={handleCardClick} onDuplicateClick={onDuplicateClick} />;
};

export const CaseCard = memo(CaseCardComponent);
